import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { isBefore } from 'date-fns/isBefore';
import { type Registry as Services, service } from '@ember/service';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { BookingChannel } from 'uplisting-frontend/models/booking';
import { nightsBetween } from 'uplisting-frontend/utils/date';
import { getPreferredMessagingChannel } from 'uplisting-frontend/utils/messaging-channels';

export enum BookingRequestStatus {
  accepted = 'accepted',
  approved = 'approved',
  cancelled = 'cancelled',
  denied = 'denied',
  pending = 'pending',
  rejected = 'rejected',
  timedout = 'timedout',
  externallyRemoved = 'externally_removed',
}

export enum BookingRequestRejectionReason {
  datesNotAvailable = 'dates_not_available',
  notAGoodFit = 'not_a_good_fit',
  waitingForBetterReservation = 'waiting_for_better_reservation',
  notComfortable = 'not_comfortable',
}

export default class BookingRequestModel extends Model {
  @service translations!: Services['translations'];

  @attr('string') checkIn!: string;
  @attr('string') checkOut!: string;
  @attr('string') guestName!: string;
  @attr('string') guestEmail!: string;
  @attr('number') numberOfGuests!: number;
  @attr('string') status!: BookingRequestStatus;
  @attr('string') channel!: BookingChannel;
  @attr('string') externalReservationId!: string;
  @attr('string') externalReservationLink!: string;
  @attr('string') rejectionReason!: BookingRequestRejectionReason;
  @attr('string') rejectionMessage!: string;
  @attr('date') expiresAt?: Date;

  @belongsTo('calendar', { inverse: 'bookingRequests', async: false })
  calendar!: ModelRegistry['calendar'];

  @belongsTo('property', { inverse: 'bookingRequests', async: false })
  property!: ModelRegistry['property'];

  @belongsTo('multi-unit', { inverse: 'bookingRequests', async: false })
  multiUnit!: ModelRegistry['multi-unit'];

  @hasMany('messaging-channel', { inverse: 'bookingRequest', async: false })
  messagingChannels!: ModelRegistry['messaging-channel'][];

  @cached
  get preferredMessagingChannel(): string {
    return getPreferredMessagingChannel(this.channel, this.messagingChannels);
  }

  @belongsTo('channel-user', { async: false, inverse: null })
  channelUser!: ModelRegistry['channel-user'];

  @belongsTo('ai-occasion-context', { inverse: null, async: false })
  aiOccasionContext?: ModelRegistry['ai-occasion-context'];

  @belongsTo('price', { inverse: null, async: false })
  price!: ModelRegistry['price'];

  @belongsTo('inbox-message', { inverse: 'bookingRequests', async: false })
  inboxMessage!: ModelRegistry['inbox-message'];

  @hasMany('message', { async: false, inverse: 'bookingRequest' })
  messages!: ModelRegistry['message'][];

  @cached
  get duration(): number {
    return nightsBetween(this.checkIn, this.checkOut);
  }

  @cached
  get displayChannel(): string {
    if (this.channel) {
      return this.translations.getChannelNameFor(this.channel);
    }

    return this.channel;
  }

  @cached
  get isAirbnbOfficial(): boolean {
    return this.channel === BookingChannel.airbnbOfficial;
  }

  @cached
  get isUplisting(): boolean {
    return this.channel === BookingChannel.uplisting;
  }

  @cached
  get isDirectRtb(): boolean {
    return this.isUplisting;
  }

  @cached
  get hasExpired(): boolean {
    return !!this.expiresAt && this.expiresAt.getTime() <= Date.now();
  }

  @cached
  get isVrbo(): boolean {
    return this.channel === BookingChannel.homeAway;
  }

  @cached
  get isBookingDotCom(): boolean {
    return this.channel === BookingChannel.bookingDotCom;
  }

  @cached
  get messagesSorted(): ModelRegistry['message'][] {
    return this.messages.slice().sort((x, y) => {
      if (isBefore(x.createdAt, y.createdAt)) {
        return -1;
      } else if (isBefore(y.createdAt, x.createdAt)) {
        return 1;
      }

      return 0;
    });
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'booking-request': BookingRequestModel;
  }
}
